import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { sortedSponsors } from '../../data/sponsors';
import SponsorCard from '../shared/SponsorCard';
import './SponsorsSection.css';

const BASE_SPEED = 62; // px/s ambient drift (content moves left)
const FAST_SPEED = 340; // px/s while holding an arrow button
const CARD_STEP = 372; // one card + gap, for tap/keyboard nudges

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Homepage sponsor strip: a continuous marquee of all sponsors (Diamond -> Bronze).
//
// The track holds N identical groups (N even, measured so the track spans at
// least twice the container — seamless on ultrawide screens and with few
// sponsors). Only the first group is in the tab order; the rest are aria-hidden
// clones. The transform is driven by requestAnimationFrame so the arrows can
// accelerate or reverse it on demand:
//   - the strip is at rest (Diamond sponsor first, fully visible) until the
//     section scrolls into view; the ambient drift starts on first entry and
//     pauses again whenever the section is off-screen
//   - ambient drift leftwards at BASE_SPEED
//   - hover, keyboard focus, or press-and-hold on the strip pauses it
//   - holding an arrow scrolls fast in that direction; a tap/click (or
//     keyboard activation) glides by one card instead
//   - focusing a card that is translated out of view repositions the strip
// prefers-reduced-motion renders a static wrapped grid instead (see CSS) and
// the animation loop and arrows are disabled entirely.
const SponsorsSection = () => {
  const marqueeRef = useRef(null);
  const trackRef = useRef(null);
  const groupRef = useRef(null);
  const [copies, setCopies] = useState(2);
  const [imagesReady, setImagesReady] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  // Per-frame animation state lives in refs — never triggers re-renders.
  const offsetRef = useRef(0);
  const speedRef = useRef(0);
  const impulseRef = useRef(0); // pending glide distance from taps
  const holdDirRef = useRef(0); // +1 forward (right arrow), -1 backward (left)
  const downTimeRef = useRef(0);
  const hoverRef = useRef(false);
  const focusRef = useRef(false);
  const holdPauseRef = useRef(false); // press-and-hold on the strip itself
  const inViewRef = useRef(false); // strip drifts only while in the viewport
  const readyRef = useRef(false); // stays at rest until every logo has loaded
  const lastTxRef = useRef(0);
  const halfLoopRef = useRef(0); // px distance of one seamless loop
  const reducedMotion = prefersReducedMotion();

  // Measure container + one group; render enough clones that the track spans
  // at least 2x the container, and record the loop length for wrapping.
  useLayoutEffect(() => {
    const marquee = marqueeRef.current;
    const group = groupRef.current;
    if (!marquee || !group) return undefined;

    const measure = () => {
      const groupW = group.offsetWidth;
      const containerW = marquee.offsetWidth;
      if (!groupW || !containerW) return;
      const needed = Math.max(2, 2 * Math.ceil(containerW / groupW));
      setCopies(needed);
      halfLoopRef.current = (needed / 2) * groupW;
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(marquee);
    observer.observe(group); // re-measures once images/fonts settle
    return () => observer.disconnect();
  }, []);

  // Start/stop the drift with viewport visibility, so a visitor scrolling down
  // always meets the strip at rest with the top-tier sponsor first.
  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!marquee || typeof IntersectionObserver === 'undefined') {
      inViewRef.current = true; // fail open: no observer support, just scroll
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => { inViewRef.current = entry.isIntersecting; },
      { threshold: 0.3 }
    );
    observer.observe(marquee);
    return () => observer.disconnect();
  }, []);

  // Preload every sponsor logo, then reveal the whole strip in one motion — no
  // logo pops in mid-scroll, and the drift stays parked (Diamond first) until
  // the section is fully formed.
  useEffect(() => {
    const srcs = [
      ...new Set(
        sortedSponsors.flatMap((sp) =>
          sp.logos ? sp.logos.map((l) => l.img) : sp.img ? [sp.img] : []
        )
      ),
    ];
    if (srcs.length === 0) {
      setImagesReady(true);
      return undefined;
    }

    let cancelled = false;
    const markReady = () => {
      if (!cancelled) setImagesReady(true);
    };
    const loadOne = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve; // a broken logo must never block the reveal
        img.src = src;
        if (img.complete) resolve(); // already cached
      });

    Promise.all(srcs.map(loadOne)).then(markReady);
    // Safety net: never hold the strip hidden longer than 2.5s on a slow asset.
    const failsafe = setTimeout(markReady, 2500);
    return () => {
      cancelled = true;
      clearTimeout(failsafe);
    };
  }, []);

  // Mirror the ready flag into a ref so the rAF loop can read it without the
  // driver effect re-subscribing.
  useEffect(() => {
    readyRef.current = imagesReady;
  }, [imagesReady]);

  // Only surface the loading cue if the wait is actually perceptible, so
  // instant (cached) loads never flash it.
  useEffect(() => {
    if (imagesReady) return undefined;
    const t = setTimeout(() => setShowLoader(true), 200);
    return () => clearTimeout(t);
  }, [imagesReady]);

  // The animation driver.
  useEffect(() => {
    if (reducedMotion) return undefined;
    let raf;
    let last = performance.now();

    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1); // clamp resumes after tab switch
      last = now;
      const track = trackRef.current;
      if (track) {
        let target;
        if (!readyRef.current) {
          target = 0; // parked at rest until the logos finish loading
        } else if (holdDirRef.current) {
          target = holdDirRef.current > 0 ? -FAST_SPEED : FAST_SPEED;
        } else if (
          !inViewRef.current ||
          hoverRef.current ||
          focusRef.current ||
          holdPauseRef.current
        ) {
          target = 0;
        } else {
          target = -BASE_SPEED;
        }
        // ease towards the target speed so starts/stops feel smooth
        speedRef.current += (target - speedRef.current) * Math.min(1, dt * 8);

        let offset = offsetRef.current + speedRef.current * dt;

        // consume tap impulses as an exponential glide
        if (impulseRef.current !== 0) {
          const step = impulseRef.current * Math.min(1, dt * 6);
          offset += step;
          impulseRef.current -= step;
          if (Math.abs(impulseRef.current) < 0.5) {
            offset += impulseRef.current;
            impulseRef.current = 0;
          }
        }

        // wrap into (-halfLoop, 0] so the loop is seamless in both directions
        const half = halfLoopRef.current;
        if (half > 0) {
          while (offset <= -half) offset += half;
          while (offset > 0) offset -= half;
        }
        offsetRef.current = offset;
        if (Math.abs(offset - lastTxRef.current) > 0.01) {
          track.style.transform = `translate3d(${offset}px, 0, 0)`;
          lastTxRef.current = offset;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reducedMotion]);

  // Safety: never leave a hold active if the pointer is released elsewhere.
  useEffect(() => {
    const clear = () => {
      holdDirRef.current = 0;
      holdPauseRef.current = false;
    };
    window.addEventListener('pointerup', clear);
    window.addEventListener('pointercancel', clear);
    return () => {
      window.removeEventListener('pointerup', clear);
      window.removeEventListener('pointercancel', clear);
    };
  }, []);

  // Keyboard support: when focus lands on a card whose visual position is
  // outside the strip, reposition the strip so the card is centred.
  const handleFocusIn = useCallback((e) => {
    focusRef.current = true;
    if (reducedMotion) return;
    const marquee = marqueeRef.current;
    const track = trackRef.current;
    const card = e.target.closest && e.target.closest('.sponsor-card');
    if (!marquee || !track || !card) return;

    const mRect = marquee.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const margin = 60; // keep clear of the edge fade mask
    if (cRect.left >= mRect.left + margin && cRect.right <= mRect.right - margin) {
      return; // already fully visible
    }

    const half = halfLoopRef.current;
    if (!half) return;
    const cardLayoutLeft = cRect.left - mRect.left - offsetRef.current;
    let desired = mRect.width / 2 - (cardLayoutLeft + cRect.width / 2);
    desired %= half;
    if (desired > 0) desired -= half;
    offsetRef.current = desired;
    impulseRef.current = 0;
    track.style.transform = `translate3d(${desired}px, 0, 0)`;
  }, [reducedMotion]);

  const handleFocusOut = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) focusRef.current = false;
  }, []);

  // Arrow buttons: hold to fast-scroll, tap (or keyboard-activate) to glide
  // one card. dir is +1 for forward (right), -1 for backward (left).
  const armHold = (dir) => () => {
    downTimeRef.current = performance.now();
    holdDirRef.current = dir;
  };
  const nudge = (dir) => (e) => {
    const heldMs = performance.now() - downTimeRef.current;
    // e.detail === 0 -> keyboard activation; short presses count as taps
    if (e.detail === 0 || heldMs < 250) {
      impulseRef.current += dir * -CARD_STEP;
    }
  };

  return (
    <section className="sponsors section fade-in-up" id="sponsors">
      <div className="container" id="sponsor-container">
        <span className="badge" id="sponsor-badge">Special Thanks to</span>
        <h2 className="section-title" id="sponsor-title">Our Sponsors</h2>
        <p className="sponsor-subtitle">
          We are grateful to the following organisations for their generous support of ICES2026.
        </p>
      </div>
      <div className={`sponsor-marquee-shell${imagesReady ? ' is-ready' : ''}`}>
        <div
          className="sponsor-marquee"
          ref={marqueeRef}
          onPointerEnter={() => { hoverRef.current = true; }}
          onPointerLeave={() => { hoverRef.current = false; }}
          onPointerDown={() => { holdPauseRef.current = true; }}
          onFocus={handleFocusIn}
          onBlur={handleFocusOut}
        >
          <div className="sponsor-marquee__track" ref={trackRef}>
            {Array.from({ length: copies }, (_, g) => (
              <div
                className="sponsor-marquee__group"
                aria-hidden={g > 0 ? 'true' : undefined}
                ref={g === 0 ? groupRef : undefined}
                key={g}
              >
                {sortedSponsors.map((sp) => (
                  <SponsorCard sponsor={sp} variant="marquee" isClone={g > 0} key={sp.name} />
                ))}
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="sponsor-marquee-arrow sponsor-marquee-arrow--left"
          aria-label="Scroll sponsors backwards"
          onPointerDown={armHold(-1)}
          onClick={nudge(-1)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          type="button"
          className="sponsor-marquee-arrow sponsor-marquee-arrow--right"
          aria-label="Scroll sponsors forwards"
          onPointerDown={armHold(1)}
          onClick={nudge(1)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
        <div
          className={`sponsor-marquee-loader${showLoader && !imagesReady ? ' is-visible' : ''}`}
          aria-hidden="true"
        >
          <span className="sponsor-marquee-loader__dot" />
          <span className="sponsor-marquee-loader__dot" />
          <span className="sponsor-marquee-loader__dot" />
        </div>
      </div>
      <div className="sponsor-view-all">
        <Link to="/sponsors" className="btn btn-outline">
          View All Sponsors
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14" />
            <path d="M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </section>
  );
};

export default SponsorsSection;
